import React from "react";
import { Box, Typography, Grid, Paper, Stack, Chip } from "@mui/material";
import { useLogger } from "../../../../contexts/LoggerContext";
import { useSchemes } from "../../../../contexts/SchemesContext";
import {
  DefensivePlayLog,
  DefensivePlaySelection,
  DefensivePlayCall,
} from "../../../../types/LogTypes";
import { jerseyNumberNameDisplay } from "../../../../helpers/helpers";

export const DefenseDetails = () => {
  const { currentPlayCall, currentPlayLog, currentPlayPenalty, touchdown, safety } = useLogger();
  const { defensivePersonnel, fronts, stunts, coverages } = useSchemes();

  const playCall = currentPlayCall as DefensivePlaySelection | null;
  const playLog = currentPlayLog as DefensivePlayLog | null;

  const personnel_group = playCall
    ? defensivePersonnel.find((p) => p.id === playCall.personnel_group_id)
    : null;
  const front = playCall
    ? fronts.find((f) => f.id === playCall.front_id)
    : null;
  const stunt = playCall
    ? stunts.find((s) => s.id === playCall.stunt_id)
    : null;
  const coverage = playCall
    ? coverages.find((c) => c.id === playCall.coverage_id)
    : null;

  const CalculateGridSize = () => {
    if (currentPlayPenalty) {
      return 4;
    }
    return 6;
  };
  return (
    <Box sx={{ width: "100%" }}>
      <Grid container spacing={3}>
        {/* Play Call Section */}
        <Grid item xs={12} md={CalculateGridSize()}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              height: "100%",
              backgroundColor: "background.paper",
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Play Call
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Personnel
                </Typography>
                <Typography variant="h6">
                  {personnel_group?.name || ""}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Front
                </Typography>
                <Typography variant="h6">{front?.name || ""}</Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Stunt
                </Typography>
                <Typography variant="h6">{stunt?.name || ""}</Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Coverage
                </Typography>
                <Typography variant="h6">{coverage?.name || ""}</Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Play Result Section */}
        <Grid item xs={12} md={CalculateGridSize()}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              height: "100%",
              backgroundColor: "background.paper",
              borderRadius: 2,
              position: "relative",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Play Result
            </Typography>

            <Stack spacing={2}>
              {/* Gain/Loss - Touchdown - Safety */}
              <Box>
                {touchdown ? (
                  <Chip
                    label="Touchdown"
                    color="success"
                    size="small"
                    variant="outlined"
                  />
                ) : safety ? (
                  <Chip
                    label="Safety"
                    color="error"
                    size="small"
                    variant="outlined"
                  />
                ) : (
                  <Typography variant="subtitle2" color="text.secondary">
                    Gain/Loss
                  </Typography>
                )}
                <Typography
                  variant="h5"
                  color={
                    playLog && playLog.gain_loss >= 0
                      ? "error.main"
                      : "success.main"
                  }
                >
                  {playLog ? (
                    <>
                      {playLog.gain_loss > 0 ? "+" : ""}
                      {playLog.gain_loss} yards
                    </>
                  ) : (
                    "0 yards"
                  )}
                </Typography>
              </Box>

              {playLog && (
                <Stack spacing={1}>
                  {/* Play Type */}
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Play Type
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {playLog.run_pass === "R" ? "Run" : "Pass"}
                    </Typography>
                  </Box>

                  {/* Formation */}
                  {playLog.offensive_formation && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Offensive Formation
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {playLog.offensive_formation}
                      </Typography>
                    </Box>
                  )}

                  {/* Tacklers */}
                  {playLog.tackle?.primary_tackler_roster_player && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Tackler
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {jerseyNumberNameDisplay(
                          playLog.tackle.primary_tackler_roster_player
                        )}
                      </Typography>
                    </Box>
                  )}

                  {playLog.tackle?.assisted_tacklers_roster_players &&
                    playLog.tackle.assisted_tacklers_roster_players.length >
                      0 && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Assisted By
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          flexWrap="wrap"
                          gap={1}
                        >
                          {playLog.tackle.assisted_tacklers_roster_players.map(
                            (player, index) => (
                              <Chip
                                key={index}
                                label={jerseyNumberNameDisplay(player)}
                                size="small"
                                variant="outlined"
                              />
                            )
                          )}
                        </Stack>
                      </Box>
                    )}

                  {/* Pass Break Up */}
                  {playLog.pass_break_up_roster_player && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Pass Break Up
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {jerseyNumberNameDisplay(
                          playLog.pass_break_up_roster_player
                        )}
                      </Typography>
                    </Box>
                  )}

                  {/* Turnover Section */}
                  {(playLog.fumble || playLog.interception) && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 1.5,
                        backgroundColor: "success.main",
                        color: "success.contrastText",
                        borderRadius: 1,
                      }}
                    >
                      {playLog.fumble && (
                        <>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600 }}
                          >
                            FUMBLE RECOVERY
                          </Typography>
                          {playLog.fumble.fumble_recovery_roster_player && (
                            <Typography variant="body2">
                              Recovered by:{" "}
                              {jerseyNumberNameDisplay(
                                playLog.fumble.fumble_recovery_roster_player
                              )}
                            </Typography>
                          )}
                        </>
                      )}
                      {playLog.interception && (
                        <>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600 }}
                          >
                            INTERCEPTION
                          </Typography>
                          {playLog.interception.interception_roster_player && (
                            <Typography variant="body2">
                              Intercepted by:{" "}
                              {jerseyNumberNameDisplay(
                                playLog.interception.interception_roster_player
                              )}
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                  )}
                </Stack>
              )}
            </Stack>
          </Paper>
        </Grid>

        {/* Penalties Section - Only shown if there are penalties */}
        {currentPlayPenalty && (
          <Grid item xs={12} md={CalculateGridSize()}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                backgroundColor: "warning.light",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{ color: "warning.dark", fontWeight: 600 }}
              >
                Penalties
              </Typography>
              {/* Add penalty details here */}
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
